import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { platformApi, API_BASE } from '@/lib/api';
import { useQueryClient } from '@tanstack/react-query';
import type { PlatformUser, SupportInfo } from '@/lib/types';

interface PlatformAuthContextType {
  user: PlatformUser | null;
  support: SupportInfo | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<void>;
  enterSupport: (organizationId: string, reason: string) => Promise<SupportInfo>;
  exitSupport: () => Promise<void>;
}

const PlatformAuthContext = createContext<PlatformAuthContextType | null>(null);

export function PlatformAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PlatformUser | null>(null);
  const [support, setSupport] = useState<SupportInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  const refresh = useCallback(async () => {
    try {
      const token = sessionStorage.getItem("platform_access_token");
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const response = await fetch(`${API_BASE}/platform/auth/me`, { headers, credentials: 'include' });
      if (response.status === 401) {
        sessionStorage.removeItem("platform_access_token");
        setUser(null);
        setSupport(null);
        return;
      }
      if (!response.ok) { setUser(null); setSupport(null); return; }
      const body = await response.json();
      if (body.success) {
        setUser(body.user ?? null);
        setSupport(body.support ?? null);
      } else {
        setUser(null);
        setSupport(null);
      }
    } catch {
      setUser(null);
      setSupport(null);
    }
  }, []);

  const bootstrapRef = useRef(false);

  useEffect(() => {
    if (bootstrapRef.current) return;
    bootstrapRef.current = true;

    // A visitor with no stored token has nothing to validate. Skip the
    // /platform/auth/me probe entirely so the public Login page never emits
    // auth errors or 401s just because the user has not signed in yet.
    // Returning users with a token are still verified against /auth/me.
    if (!sessionStorage.getItem("platform_access_token")) {
      setUser(null);
      setSupport(null);
      setLoading(false);
      return;
    }

    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await platformApi.post<{ success: boolean; accessToken: string; user: PlatformUser }>('/platform/auth/login', { email, password });
    if (res.success && res.accessToken) {
      sessionStorage.setItem("platform_access_token", res.accessToken);
      queryClient.clear();
    }
    setUser(res.user ?? null);
    setSupport(null);
  }, [queryClient]);

  const logout = useCallback(() => {
    platformApi.post('/platform/auth/logout').finally(() => {
      sessionStorage.removeItem("platform_access_token");
      setUser(null);
      setSupport(null);
      queryClient.clear();
      window.location.href = '/super-admin/login';
    });
  }, [queryClient]);

  const enterSupport = useCallback(async (organizationId: string, reason: string) => {
    const res = await platformApi.post<{ success: boolean; token: string; support: SupportInfo }>('/platform/support/enter', { organizationId, reason });
    if (res.success && res.token) {
      sessionStorage.setItem("platform_access_token", res.token);
    }
    setSupport(res.support ?? null);
    queryClient.clear();
    await refresh();
    return res.support;
  }, [queryClient, refresh]);

  const exitSupport = useCallback(async () => {
    const res = await platformApi.post<{ success: boolean; token: string }>('/platform/support/exit');
    if (res.success && res.token) {
      sessionStorage.setItem("platform_access_token", res.token);
    }
    setSupport(null);
    queryClient.clear();
    await refresh();
  }, [queryClient, refresh]);

  return (
    <PlatformAuthContext.Provider value={{ user, support, loading, login, logout, refresh, enterSupport, exitSupport }}>
      {children}
    </PlatformAuthContext.Provider>
  );
}

export function usePlatformAuth() {
  const ctx = useContext(PlatformAuthContext);
  if (!ctx) throw new Error('usePlatformAuth must be used within PlatformAuthProvider');
  return ctx;
}
